import { autoUpdate, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react'
import { cloneElement, isValidElement } from 'react'

import type { CloseEvent, TutorialProps } from './tutorial.types'

import { cs } from '../utils'
import { TutorialCard } from './tutorialCard'

/** Popover Tutorial is used to display information about a new, or existing feature. */
export function Tutorial({
  actionLabel,
  children,
  className,
  onAction,
  onDismiss,
  onFinish,
  steps,
  title,
  placement = 'right',
  showSkipButton,
  isVisible = false,
  includeFinalPage = true,
  useContentTitle = false,
}: TutorialProps) {
  const onClose = (e: CloseEvent) => {
    if (e === 'dismissed') onDismiss?.()
    if (e === 'finished') onFinish?.()
  }

  const { refs, floatingStyles } = useFloating({
    open: isVisible,
    placement,
    middleware: [
      offset({ mainAxis: 2, crossAxis: 0 }),
      shift({ padding: 5 }),
      // No flip - disabled in original
    ],
    whileElementsMounted: isVisible ? autoUpdate : undefined,
  })

  // Clone the child element to add ref
  const childElement = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        ref: refs.setReference,
      })
    : children

  return (
    <>
      {childElement}
      {isVisible && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles}>
            <TutorialCard
              actionLabel={actionLabel}
              className={cs('vui-tutorial', className)}
              onAction={onAction}
              onDismiss={() => onClose('dismissed')}
              onFinish={() => onClose('finished')}
              showSkipButton={showSkipButton}
              steps={steps}
              title={title}
              includeFinalPage={includeFinalPage}
              useContentTitle={useContentTitle}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Tutorial.displayName = 'Tutorial'
